import { useState, useRef, useEffect, useContext } from "react";
import { gsap } from "gsap";
import Logo from "../image/logo.png";
import logo_black from "../image/logo-footer.png";
import AuthModal from "./modals/authModal";
import { UserDataContext } from "../context/User";
import { useNavigate } from "react-router-dom";
import UserDropdown from "./Userdropdown";
import NotificationDropdown from "./NotificationDropdown";

const Hero = ({ isBlack }) => {
  const navigate = useNavigate();
  const { userData, logOut } = useContext(UserDataContext);
  const [isOpen, setIsOpen] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const headerRef = useRef(null);
  const navRef = useRef(null);
  const buttonRef = useRef(null);
  const mobileMenuRef = useRef(null);

  const publicItems = [
    { label: "Home", href: "/lwbl/#home" },
    { label: "About", href: "/lwbl/#about" },
    { label: "Gallery", href: "/lwbl/#gallery" },
    { label: "Quests & Challenges", href: "/lwbl/#quests" },
    { label: "Refine Education", href: "/lwbl/#refine" },
    { label: "Blogs", href: "/lwbl/#learn" },
    { label: "Community", href: "/lwbl/#community" },
    { label: "Shop", href: "/lwbl/shop" },
    { label: "Contact", href: "/lwbl/contact" },
    { label: "Membership", href: "/lwbl/membership" },
  ];

  const menuItems = publicItems;

  useEffect(() => {
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({ defaults: { ease: "power3.out" } });
      tl.from(headerRef.current, { y: -80, opacity: 0, duration: 1 });
      if (navRef.current) {
        const links = navRef.current.querySelectorAll("a");
        tl.from(links, { y: 0, opacity: 0, stagger: 0.08, duration: 0.5 }, "-=0.7");
      }
      if (buttonRef.current) {
        tl.from(buttonRef.current, { y: 0, opacity: 0, duration: 0.5 }, "-=0.4");
      }
    }, headerRef);
    return () => ctx.revert();
  }, []);

  useEffect(() => {
    if (mobileMenuRef.current) {
      if (isOpen) {
        gsap.fromTo(
          mobileMenuRef.current,
          { height: 0, opacity: 0 },
          { height: "auto", opacity: 1, duration: 0.4, ease: "power2.out" }
        );
      } else {
        gsap.to(mobileMenuRef.current, {
          height: 0, opacity: 0, duration: 0.3, ease: "power2.in",
        });
      }
    }
  }, [isOpen]);

  return (
    <header
      ref={headerRef}
      className="flex items-center gap-[25px] absolute top-0 left-0 w-full z-20 p-5"
    >
      <div className="container mx-auto w-full px-6 py-4 xs:px-2 flex items-center justify-between">
        <div className="flex items-center space-x-2">
          {isBlack ? (
            <img src={logo_black} alt="Company Logo" className="w-30 h-10 lg:w-60 lg:h-25 object-contain" />
          ) : (
            <img src={Logo} alt="Company Logo" className="w-30 h-10 lg:w-60 lg:h-25 object-contain" />
          )}
        </div>

        <nav ref={navRef} className="hidden lg:flex space-x-8">
          {menuItems.map((item) => (
            <a
              key={item.label}
              href={item.href}
              className={`${isBlack ? "text-black" : "text-white"} font-outfit hover:text-[#B6FCFF] transition-colors duration-300`}
            >
              {item.label}
            </a>
          ))}
        </nav>

        {userData && userData.id ? (
          <div className="hidden lg:flex items-center gap-3">
            <NotificationDropdown isBlack={isBlack} />
            <UserDropdown userData={userData} logOut={logOut} />
          </div>
        ) : (
          <div className="hidden lg:flex items-center gap-3">
            <button
              onClick={() => navigate("/login")}
              className="px-6 py-2 rounded-full border border-[#00C3FF] text-[#00C3FF] font-semibold font-outfit transition-all duration-300 hover:bg-[#00C3FF] hover:text-black"
            >
              Login
            </button>
            <button
              onClick={() => navigate("/register")}
              className="px-6 py-2 rounded-full bg-gradient-to-r from-[#B6FCFF] to-[#00C3FF] text-black font-semibold font-outfit transition-transform duration-300 hover:scale-105"
            >
              Register
            </button>
          </div>
        )}

        <button
          className="lg:hidden text-white focus:outline-none"
          aria-label="Toggle menu"
          onClick={() => setIsOpen((prev) => !prev)}
        >
          <span className={`text-2xl ${isBlack ? "text-black" : "text-white"}`}>
            {isOpen ? "✖" : "☰"}
          </span>
        </button>
      </div>

      <div
        ref={mobileMenuRef}
        className={`fixed top-[80%] left-0 h-full bg-gray-50 p-6 text-gray-700
          transition-transform duration-500 ease-in-out
          ${isOpen ? "translate-x-0" : "-translate-x-full"} w-full z-50 flex flex-col`}
      >
        <nav className="flex flex-col items-start gap-3">
          {menuItems.map((item) => (
            <a
              key={item.label}
              href={item.href}
              className="block py-2 hover:text-blue-500 transition-colors duration-300"
              onClick={() => setIsOpen(false)}
            >
              {item.label}
            </a>
          ))}
        </nav>

        <div className="flex flex-col gap-3 w-full mt-6 pt-6 border-t border-gray-300">
          {userData ? (
            <button
              onClick={() => { logOut(); setIsOpen(false); }}
              className="w-full bg-white border border-gray-300 text-gray-700 py-2 rounded-lg font-semibold font-outfit"
            >
              Log out
            </button>
          ) : (
            <>
              <button
                onClick={() => { navigate("/login"); setIsOpen(false); }}
                className="w-full bg-white border border-blue-600 text-blue-600 py-2 rounded-lg hover:bg-blue-50 transition-colors duration-300 font-semibold font-outfit"
              >
                Login
              </button>
              <button
                onClick={() => { navigate("/register"); setIsOpen(false); }}
                className="w-full bg-gradient-to-r from-[#B6FCFF] to-[#00C3FF] text-black py-2 rounded-lg hover:opacity-90 transition-opacity duration-300 font-semibold font-outfit"
              >
                Register
              </button>
            </>
          )}
        </div>
      </div>

      <AuthModal open={authOpen} setOpen={setAuthOpen} />
    </header>
  );
};

export default Hero;
